import {
  Navigate,
  useLocation,
  useParams,
  type RouteObject,
} from "react-router-dom";

type AnalyticsProductScope = "all" | "returns" | "order-editing";

function getAnalyticsUrl(
  productScope: AnalyticsProductScope,
  search: string,
  section?: string,
): string {
  const searchParams = new URLSearchParams(search);
  if (productScope === "all") {
    searchParams.delete("product");
  } else {
    searchParams.set("product", productScope);
  }
  if (productScope !== "returns") {
    searchParams.delete("section");
  } else if (section === "overview") {
    searchParams.delete("section");
  } else if (section != null) {
    searchParams.set("section", section);
  }

  const nextSearch = searchParams.toString();
  return `/onboardings/analytics${nextSearch ? `?${nextSearch}` : ""}`;
}

function LegacyAnalyticsRedirect() {
  const { search } = useLocation();
  const params = useParams();
  const slice = params.slice ?? "overview";
  let productScope: AnalyticsProductScope;
  switch (slice) {
    case "overview": {
      productScope = "all";
      break;
    }
    case "returns": {
      productScope = "returns";
      break;
    }
    case "order-editing":
    case "oms": {
      productScope = "order-editing";
      break;
    }
    default:
      productScope = "all";
  }
  return (
    <Navigate replace to={getAnalyticsUrl(productScope, search, params.tab)} />
  );
}

export const onboardingsRoutes: RouteObject[] = [
  {
    handle: { breadcrumb: "Onboardings" },
    async lazy() {
      const { Page } = await import("../app/page");
      return { element: <Page hidePadding={() => true} /> };
    },
    children: [
      { index: true, element: <Navigate replace to="merchants" /> },

      {
        path: "merchants",
        handle: { breadcrumb: "Merchants" },
        children: [
          {
            index: true,
            async lazy() {
              const { V2MerchantsPage } = await import("./v2/V2MerchantsPage");
              return { Component: V2MerchantsPage };
            },
          },
          {
            path: "view/:teamId",
            handle: { breadcrumb: "Details" },
            async lazy() {
              const { V2MerchantDetailPage } =
                await import("./v2/V2MerchantDetailPage");
              return { Component: V2MerchantDetailPage };
            },
          },
        ],
      },

      {
        path: "recover-registrations",
        handle: { breadcrumb: "Recover registrations" },
        async lazy() {
          const { RecoverRegistrationsTab } =
            await import("./recover/registrations/RecoverRegistrationsTab");
          return { Component: RecoverRegistrationsTab };
        },
      },

      {
        path: "analytics",
        handle: { breadcrumb: "Analytics" },
        async lazy() {
          const { AnalyticsPage } = await import("./analytics/AnalyticsPage");
          return { Component: AnalyticsPage };
        },
      },
      // Pre-query-param analytics URLs (`/analytics/returns`, `/analytics/oms`, …).
      { path: "analytics/:slice", element: <LegacyAnalyticsRedirect /> },
      { path: "analytics/:slice/:tab", element: <LegacyAnalyticsRedirect /> },

      {
        path: "settings",
        handle: { breadcrumb: "Settings" },
        children: [
          { index: true, element: <Navigate replace to="sync" /> },
          {
            path: ":section",
            async lazy() {
              const { SettingsPage } = await import("./settings/SettingsPage");
              return { Component: SettingsPage };
            },
          },
        ],
      },
    ],
  },
];
